// g2-decision-with-high-water-marks.ts
//
// Measurement prelude for ensureG2DecisionRecorded, split out of
// g2-dispatch-gate-enforcement.ts to keep that file ≤200 lines. Ratchets both
// §8 high-water marks (Bug L plan size, P17 baseline exceptions) BEFORE
// calling the pure decideG2Requirement, then returns its verdict — so the
// enforcement file only has to persist the outcome.
import {
  countDeclaredBaselineExceptions,
  readG2BaselineExceptionMark,
  recordG2BaselineExceptionMark,
} from "./g2-baseline-exception-mark.js";
import {
  countEffectivePlanTickets,
  decideG2Requirement,
  type G2Decision,
} from "./g2-plan-gate-decision.js";
import {
  readG2PlanSizeHighWaterMark,
  recordG2PlanSizeHighWaterMark,
} from "./g2-plan-size-high-water-mark.js";
import { readMissionDeclaredResources } from "./mission-declared-resources.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export interface MeasureG2DecisionInput {
  controlRoot: string;
  /** Execution repo worktree — source of `.avatar/verification-profile.json` (§8 condition 3). */
  repoDir: string;
  tickets: Ticket[];
  ticketStatus: Record<string, string>;
  g2TicketThreshold: number;
  g0Delegated: boolean;
}

/**
 * Records this tick's plan-size/baseline-exception observations (both
 * ratchet-up-only high-water marks), reads the person-declared resource
 * boundary, then returns `decideG2Requirement`'s verdict over all of it. No
 * gate persistence here — the caller decides what to do with the decision.
 */
export async function measureG2Decision(input: MeasureG2DecisionInput): Promise<G2Decision> {
  // Bug L (D16): record the plan's size BEFORE deciding, so this tick's own
  // observation counts. Written every tick but only ever raised, so a plan
  // that shrinks later cannot walk the count condition back down.
  const livePlanSize = countEffectivePlanTickets(input.tickets, input.ticketStatus);
  await recordG2PlanSizeHighWaterMark(input.controlRoot, livePlanSize);
  const planSizeHighWaterMark = await readG2PlanSizeHighWaterMark(input.controlRoot);

  // P17 §8 condition 3, same ratchet for the same reason: the profile this
  // reads from lives in the worker's worktree, so only the peak is trusted.
  const liveBaselineExceptions = await countDeclaredBaselineExceptions(input.repoDir);
  await recordG2BaselineExceptionMark(input.controlRoot, liveBaselineExceptions);
  const baselineExceptionHighWaterMark = await readG2BaselineExceptionMark(input.controlRoot);

  // P25 §8 condition 4. Read from the CONTROL ROOT, not the worktree: the
  // boundary is the person's declaration at init and a worker must not be
  // able to edit the thing it is judged by (the Bug K shape). No high-water
  // mark needed for the same reason — the value cannot move during a mission.
  const declaredResourceNames = await readMissionDeclaredResources(input.controlRoot);

  return decideG2Requirement({
    tickets: input.tickets,
    ticketStatus: input.ticketStatus,
    g2TicketThreshold: input.g2TicketThreshold,
    g0Delegated: input.g0Delegated,
    planSizeHighWaterMark,
    baselineExceptionHighWaterMark,
    declaredResourceNames,
  });
}
